import { useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ProviderDto, WorkStatusEnum, ServiceTypeStatusEnum } from '@families-link/shared';
import { Verified } from '../../_core/constants/icons.constants';
import { Avatar, Typography, Box, Skeleton } from '@mui/material';
import { getObjectLanguage } from '../../_core/components/languages-component';
import { ProvidersTableHandlers } from '../components/providers-table';
import {
	AllowedProviderActions,
	ProvidersTableActions,
} from '../components/providers-table/providers-table-actions.component';
import { mergeSx } from 'merge-sx';
import { tableCellTypographyOverflowStyles } from '../../_core/styles';
import { InHouseIcon, OutsourceIcon } from '../../_core/components/icons';
import { compact } from 'lodash';

/*
  !IMPORTANT!
  Order in property columnsList define order of columns in table.
 */

export type ProviderColumnName =
	| 'fullName'
	| 'targetLanguage'
	| 'verificationType'
	| 'workStatus'
	| 'serviceType'
	| 'totalEngagements'
	| 'district'
	| 'actions'
	| 'importedRow';

const helper = createColumnHelper<ProviderDto & { createdAt: string; updatedAt: string }>();

export function useProvidersTableColumns(
	handlers: ProvidersTableHandlers,
	providerColumnsList: ProviderColumnName[],
	allowedProviderActions: AllowedProviderActions[],
	isSorted = true,
	isSkeletonLoader = false
): ColumnDef<ProviderDto, string>[] {
	const { onView, onDelete, onEdit } = handlers;

	const [t] = useTranslation();
	const result = useMemo(
		() =>
			compact(
				providerColumnsList.map((el: ProviderColumnName) => {
					switch (el) {
						case 'importedRow':
							return helper.accessor('importedRow', {
								header: 'Row',
								enableSorting: isSorted,
								meta: {
									flex: 0.5,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => (
									<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
										{getValue()}
									</Typography>
								),
							}) as ColumnDef<ProviderDto, string>;
						case 'fullName':
							return helper.accessor('fullName', {
								header: t('providers.full-name'),
								meta: {
									flex: 1.5,
									useCustomOverflowContainer: true,
								},
								enableSorting: isSorted,
								cell: ({ getValue, row }) => {
									const imageUrl = row.original.imageUrl;
									const fullName = getValue();

									return isSkeletonLoader ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Skeleton variant="circular" width={24} height={24} />
											<Skeleton width="60%" />
										</Box>
									) : (
										<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '6px' }}>
											<Avatar src={imageUrl} alt={fullName} style={{ width: 24, height: 24, borderRadius: '6px' }} />
											<Typography
												data-overflow="true"
												sx={mergeSx({ fontWeight: 500 }, tableCellTypographyOverflowStyles)}
											>
												{fullName}
											</Typography>
										</Box>
									);
								},
							}) as ColumnDef<ProviderDto, string>;
						case 'targetLanguage':
							return helper.accessor('targetLanguage', {
								header: t('providers.target-lang'),
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: (info) => {
									const languages = info.getValue() as string[];
									const options = languages.map(getObjectLanguage);

									return isSkeletonLoader ? (
										<Skeleton />
									) : (
										options.map((option) => (option?.name ? option.name : '')).join(', ')
									);
								},
							}) as ColumnDef<ProviderDto, string>;
						case 'verificationType':
							return helper.accessor('verificationType', {
								header: t('providers.type'),
								meta: {
									flex: 0.8,
									useCustomOverflowContainer: true,
								},
								enableSorting: true,
								cell: (info) =>
									info.getValue() && isSkeletonLoader ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Skeleton width="70%" />
											<Skeleton variant="circular" width={24} height={24} />
										</Box>
									) : info.getValue() ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
												{t('providers.verified')}{' '}
											</Typography>
											<Verified />
										</Box>
									) : isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
											{t('providers.no-verified')}
										</Typography>
									),
							}) as ColumnDef<ProviderDto, string>;
						case 'workStatus':
							return helper.accessor('workStatus', {
								header: t('providers.status'),
								meta: {
									flex: 0.8,
									useCustomOverflowContainer: true,
								},
								enableSorting: true,
								cell: ({ getValue }) => {
									switch (getValue()) {
										case WorkStatusEnum.InHouse:
											return isSkeletonLoader ? (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
													<Skeleton variant="rectangular" width={12} height={12} />
													<Skeleton width="70%" />
												</Box>
											) : (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
													<InHouseIcon sx={{ fontSize: '14px' }} />
													<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
														{t('providers.work-in-house')}
													</Typography>
												</Box>
											);
											break;
										case WorkStatusEnum.Outsource:
											return isSkeletonLoader ? (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
													<Skeleton variant="rectangular" width={12} height={12} />
													<Skeleton width="70%" />
												</Box>
											) : (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
													<OutsourceIcon sx={{ fontSize: '14px' }} />
													<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
														{t('providers.work-outsource')}
													</Typography>
												</Box>
											);
										default:
											return null;
									}
								},
							}) as ColumnDef<ProviderDto, string>;
						case 'serviceType':
							return helper.accessor('serviceType', {
								header: t('providers.service-type'),
								meta: {
									flex: 1.2,
									useCustomOverflowContainer: true,
								},
								enableSorting: isSorted,
								cell: (info) => {
									const value = info.getValue();
									switch (value) {
										case ServiceTypeStatusEnum.Appointment:
											return isSkeletonLoader ? (
												<Skeleton />
											) : (
												<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
													{t('providers.appointment')}
												</Typography>
											);
										case ServiceTypeStatusEnum.Project:
											return isSkeletonLoader ? (
												<Skeleton />
											) : (
												<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
													{t('providers.project')}
												</Typography>
											);
										case ServiceTypeStatusEnum.Both:
											return isSkeletonLoader ? (
												<Skeleton />
											) : (
												<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
													{t('providers.appointment')}, {t('providers.project')}
												</Typography>
											);
										default:
											return null;
									}
								},
							}) as ColumnDef<ProviderDto, string>;
						case 'totalEngagements':
							return helper.accessor('totalEngagements', {
								header: 'Total Engagements',
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: ({ getValue }) => isSkeletonLoader ? <Skeleton /> : getValue(),
							}) as ColumnDef<ProviderDto>;
						case 'district':
							return helper.accessor('district', {
								header: 'District',
								meta: {
									flex: 1,
								},
								enableSorting: isSorted,
								cell: ({ getValue }) => {
									const district = getValue();

									return isSkeletonLoader ? <Skeleton /> : district?.name;
								},
							}) as ColumnDef<ProviderDto, string>;
						case 'actions':
							return helper.display({
								id: 'actions',
								cell: (props) => (
									<ProvidersTableActions
										provider={props.row.original}
										allowedProviderActions={allowedProviderActions}
										onView={onView}
										onDelete={onDelete}
										onEdit={onEdit}
									/>
								),
							}) as ColumnDef<ProviderDto>;

						default:
							return undefined;
					}
				})
			),
		[t, onView, onDelete, onEdit]
	);

	return result;
}
